import { ARENA_H, ARENA_W, COLORS, type Color, DX, DY, type Heading } from '@xox/shared'
import { type HTMLAttributes, useEffect, useRef } from 'react'
import { useCycles } from '../store/cycles'
import { BLOCKED_FRACTION, MAX_EXTRAPOLATE_TICKS, SPARK_TICKS } from './anim'
import { predictSteps } from './sim'

/** Ticks after the start during which the names float over the heads. */
const LABEL_TICKS = 15

interface Palette {
  shirt: Record<Color, string>
  grid: string
  body: string
}

/** Fallbacks when the stylesheet is missing (tests, first paint before CSS). */
const FALLBACK: Record<Color, string> = {
  rose: '#f43f5e',
  orange: '#f97316',
  amber: '#f59e0b',
  lime: '#84cc16',
  emerald: '#10b981',
  sky: '#0ea5e9',
  violet: '#8b5cf6',
  pink: '#ec4899',
}

function readPalette(): Palette {
  const style = getComputedStyle(document.documentElement)
  const token = (name: string, fallback: string) => style.getPropertyValue(name).trim() || fallback
  const shirt = {} as Record<Color, string>
  for (const c of COLORS) shirt[c] = token(`--shirt-${c}`, FALLBACK[c])
  return {
    shirt,
    grid: token('--cycles-grid', 'rgba(255,255,255,0.06)'),
    body: token('--cycles-body', '#e2e8f0'),
  }
}

function reducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

type Props = HTMLAttributes<HTMLDivElement>

/**
 * The arena: two canvases (trails repainted per tick, heads redrawn every animation frame).
 * Rivals interpolate between their last two confirmed cells, one tick behind the server. The own
 * cycle runs on time and ahead along its queued turns, so a tap shows at the next cell boundary,
 * exactly when the server applies it. Pointer handlers from the input hook are spread on the box.
 */
export function CyclesArena({ className = '', ...rest }: Props) {
  const box = useRef<HTMLDivElement>(null)
  const trails = useRef<HTMLCanvasElement>(null)
  const heads = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const el = box.current
    const tc = trails.current
    const hc = heads.current
    if (!el || !tc || !hc) return
    const tctx = tc.getContext('2d')
    const hctx = hc.getContext('2d')
    if (!tctx || !hctx) return

    let palette = readPalette()
    let width = 0
    let height = 0
    let paintedRound = -1
    let paintedTick = -1
    let paintedCount = -1
    let raf = 0

    const resize = () => {
      const rect = el.getBoundingClientRect()
      const dpr = Math.min(2, window.devicePixelRatio || 1)
      width = rect.width
      height = rect.height
      for (const [c, ctx] of [
        [tc, tctx],
        [hc, hctx],
      ] as const) {
        c.width = Math.round(width * dpr)
        c.height = Math.round(height * dpr)
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      }
      paintedTick = -1
    }

    const colorOf = (id: string): string => {
      const racer = useCycles.getState().snapshot?.racers.find((r) => r.profile.id === id)
      return palette.shirt[racer?.color ?? 'sky']
    }

    const paintTrails = () => {
      const { live } = useCycles.getState()
      tctx.clearRect(0, 0, width, height)
      const cs = width / ARENA_W
      tctx.strokeStyle = palette.grid
      tctx.lineWidth = 1
      tctx.beginPath()
      for (let x = 1; x < ARENA_W; x++) {
        tctx.moveTo(x * cs, 0)
        tctx.lineTo(x * cs, height)
      }
      for (let y = 1; y < ARENA_H; y++) {
        tctx.moveTo(0, y * cs)
        tctx.lineTo(width, y * cs)
      }
      tctx.stroke()
      if (!live) return
      const colors = live.cycles.map((c) => colorOf(c.id))
      tctx.globalAlpha = 0.85
      for (let i = 0; i < live.cells.length; i++) {
        const v = live.cells[i]!
        if (v === 0) continue
        tctx.fillStyle = colors[v - 1] ?? '#fff'
        const x = i % ARENA_W
        const y = (i - x) / ARENA_W
        tctx.fillRect(x * cs + 0.5, y * cs + 0.5, cs - 1, cs - 1)
      }
      tctx.globalAlpha = 1
    }

    const head = (x: number, y: number, cs: number, color: string, own: boolean, glow: boolean) => {
      hctx.save()
      if (glow) {
        hctx.shadowColor = color
        hctx.shadowBlur = cs * 1.4
      }
      hctx.fillStyle = color
      hctx.beginPath()
      hctx.roundRect(x * cs, y * cs, cs, cs, cs * 0.3)
      hctx.fill()
      hctx.restore()
      if (own) {
        hctx.strokeStyle = 'white'
        hctx.lineWidth = Math.max(1.5, cs * 0.18)
        hctx.beginPath()
        hctx.roundRect(x * cs + 0.75, y * cs + 0.75, cs - 1.5, cs - 1.5, cs * 0.3)
        hctx.stroke()
      }
    }

    const spark = (x: number, y: number, cs: number, color: string, life: number) => {
      hctx.save()
      hctx.globalAlpha = Math.max(0, life)
      hctx.strokeStyle = color
      hctx.lineWidth = Math.max(1.5, cs * 0.22)
      const cx = (x + 0.5) * cs
      const cy = (y + 0.5) * cs
      const r = cs * (0.9 + (1 - life) * 1.6)
      hctx.beginPath()
      for (let k = 0; k < 8; k++) {
        const a = (k / 8) * Math.PI * 2
        hctx.moveTo(cx + Math.cos(a) * r * 0.35, cy + Math.sin(a) * r * 0.35)
        hctx.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r)
      }
      hctx.stroke()
      hctx.restore()
    }

    const frame = () => {
      raf = requestAnimationFrame(frame)
      const { live, snapshot, pending, playerId, clockOffset } = useCycles.getState()
      if (!live || !snapshot || width === 0) {
        if (paintedTick !== -1) {
          tctx.clearRect(0, 0, width, height)
          hctx.clearRect(0, 0, width, height)
          paintedTick = -1
        }
        return
      }
      if (
        live.no !== paintedRound ||
        live.tick !== paintedTick ||
        live.cycles.length !== paintedCount
      ) {
        paintTrails()
        paintedRound = live.no
        paintedTick = live.tick
        paintedCount = live.cycles.length
      }
      hctx.clearRect(0, 0, width, height)
      const cs = width / ARENA_W
      const glow = !reducedMotion()
      const tickMs = snapshot.tickMs
      const now = Date.now() + clockOffset
      // Fraction of the current tick that has elapsed (negative during the countdown).
      const phase = (now - (live.anchor + live.tick * tickMs)) / tickMs
      const racing = snapshot.phase === 'racing'
      const labels = snapshot.phase === 'countdown' || live.tick < LABEL_TICKS
      const queue: Heading[] = pending.map((p) => p.h)

      live.cycles.forEach((c) => {
        const color = colorOf(c.id)
        const own = c.id === playerId
        if (!c.alive) {
          head(c.x, c.y, cs, color, false, false)
          const age = live.tick - (c.diedAt ?? live.tick) + Math.max(0, Math.min(1, phase))
          if (age < SPARK_TICKS && glow) {
            const sx = Math.min(ARENA_W - 1, Math.max(0, c.x + DX[c.heading]!))
            const sy = Math.min(ARENA_H - 1, Math.max(0, c.y + DY[c.heading]!))
            spark(sx, sy, cs, color, 1 - age / SPARK_TICKS)
          }
          return
        }
        let x = c.x
        let y = c.y
        if (own && racing) {
          // Ahead of the server along the predicted path, never through a wall.
          let remaining = Math.max(0, Math.min(MAX_EXTRAPOLATE_TICKS, phase))
          const steps = predictSteps(c, queue, Math.max(1, Math.ceil(remaining)), live.cells)
          let px = c.x
          let py = c.y
          for (const s of steps) {
            if (remaining <= 0) break
            const part = Math.min(remaining, 1, s.blocked ? BLOCKED_FRACTION : 1)
            x = px + (s.x - px) * part
            y = py + (s.y - py) * part
            if (s.blocked) break
            remaining -= 1
            px = s.x
            py = s.y
          }
        } else if (racing) {
          // One tick behind: glide from the previous confirmed cell to the current one.
          const g = Math.max(0, Math.min(1, phase))
          x = c.px + (c.x - c.px) * g
          y = c.py + (c.y - c.py) * g
        }
        head(x, y, cs, color, own, glow)
        if (labels) {
          const name = snapshot.racers.find((r) => r.profile.id === c.id)?.profile.nickname ?? ''
          hctx.font = `700 ${Math.max(10, cs * 1.2)}px system-ui, sans-serif`
          hctx.textAlign = 'center'
          hctx.fillStyle = 'white'
          hctx.shadowColor = 'rgba(0,0,0,0.8)'
          hctx.shadowBlur = 4
          hctx.fillText(name, (x + 0.5) * cs, y * cs - cs * 0.6)
          hctx.shadowBlur = 0
        }
      })
    }

    const ro = new ResizeObserver(resize)
    ro.observe(el)
    resize()
    const scheme = window.matchMedia('(prefers-color-scheme: dark)')
    const onScheme = () => {
      palette = readPalette()
      paintedTick = -1
    }
    scheme.addEventListener('change', onScheme)
    const onVisibility = () => {
      cancelAnimationFrame(raf)
      if (!document.hidden) {
        raf = requestAnimationFrame(frame)
        useCycles.getState().sync()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    raf = requestAnimationFrame(frame)
    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      scheme.removeEventListener('change', onScheme)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  const snap = useCycles((s) => s.snapshot)
  const alive = useCycles((s) => s.live?.cycles.filter((c) => c.alive).length ?? 0)
  const label = snap?.round
    ? `Round ${snap.round.no}: ${alive} of ${snap.round.cycleIds.length} cycles still riding`
    : 'Empty arena'

  return (
    <div
      ref={box}
      role="img"
      aria-label={label}
      className={`relative aspect-[5/7] w-full overflow-hidden bg-[var(--cycles-bg)] shadow-inner ring-1 ring-white/10 select-none ${className}`}
      style={{ touchAction: 'none', WebkitTouchCallout: 'none' } as React.CSSProperties}
      {...rest}
    >
      <canvas ref={trails} className="absolute inset-0 size-full" />
      <canvas ref={heads} className="absolute inset-0 size-full" />
    </div>
  )
}
